import React, { useMemo } from 'react';
import styled from 'styled-components';
import KeyValueTable from './KeyValueTable';

/*
 * HoraSection — Section 7 of Pambu Sheet.
 *
 * Two rows only: Now + Next.
 * "View all 24 horas" link opens HoraDrillDown modal.
 */

const DrillDownButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.bands.exceptional};
  font-size: 13px;
  cursor: pointer;
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  text-decoration: underline;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  display: block;
  min-height: 44px;
  text-align: left;

  &:hover {
    color: ${p => p.theme.colors.text.primary};
  }

  &:focus {
    outline: 2px solid ${p => p.theme.colors.bands.exceptional};
    outline-offset: 2px;
  }
`;

function findActiveHoraIndex(horas, now) {
  if (!horas || horas.length === 0 || !now) return -1;
  const toMin = (t) => {
    if (!t) return -1;
    const parts = t.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  for (let i = 0; i < horas.length; i++) {
    const h = horas[i];
    const startMin = toMin(h.start);
    const endMin = toMin(h.end);
    if (startMin < 0 || endMin < 0) continue;
    if (endMin >= startMin) {
      if (nowMin >= startMin && nowMin < endMin) return i;
    } else {
      if (nowMin >= startMin || nowMin < endMin) return i;
    }
  }
  return -1;
}

/**
 * Planet nature for hora active-row coloring.
 * Source: UX spec phase1a-ux.md section 5.7 (Tamil Jyotish convention).
 * DO NOT modify without UX review.
 */
export const PLANET_NATURE = {
  Sun: 'bad', Moon: 'good', Mars: 'bad', Mercury: 'good',
  Jupiter: 'good', Venus: 'good', Saturn: 'bad',
  Rahu: 'neutral', Ketu: 'neutral',
};

const HoraSection = ({ horaChoghadiya, now, lang = 'en', onShowAllHoras }) => {
  const horas = horaChoghadiya?.horas || [];

  const { current, next } = useMemo(() => {
    const idx = findActiveHoraIndex(horas, now);
    if (idx < 0) return { current: null, next: null };
    return {
      current: horas[idx],
      next: horas[(idx + 1) % horas.length],
    };
  }, [horas, now]);

  if (!horaChoghadiya || horas.length === 0) return null;

  const rows = [
    {
      key: 'now',
      labelTa: 'இப்போது',
      labelEn: 'Now',
      value: current
        ? `${lang === 'ta' ? (current.planet_ta || current.planet) : current.planet}${current.end ? ` (until ${current.end.substring(0, 5)})` : ''}`
        : '—',
    },
    {
      key: 'next',
      labelTa: 'அடுத்து',
      labelEn: 'Next',
      value: next ? (lang === 'ta' ? (next.planet_ta || next.planet) : next.planet) : '—',
    },
  ];

  return (
    <>
      <KeyValueTable
        rows={rows}
        activeRowKey={current ? 'now' : null}
        lang={lang}
        accentTone={current ? (PLANET_NATURE[current.planet] || 'neutral') : undefined}
      />
      <DrillDownButton type="button" onClick={onShowAllHoras}>
        {lang === 'ta' ? '24 ஹோரைகளையும் காட்டு →' : 'View all 24 horas →'}
      </DrillDownButton>
    </>
  );
};

export default HoraSection;
